import { createSlice, type PayloadAction } from '@reduxjs/toolkit'

import { resourceScopeCleared, userScopeCleared, type SaleVerdict } from '@krp/client-runtime'

import { ownResourceKey } from '../../../ownKey'
import type { Cart, SaleProduct } from './cart'
import { initialOwnSale, ownSaleReducer, type OwnPaymentKind, type OwnSaleAction, type OwnSaleState } from './saleState'

/**
 * Продажа окна «Собственная» по источникам: корзина, выбранная строка, способ оплаты, этап продажи и итог (намерение кассира).
 * Корзину ведёт само окно (сервера корзины нет), оплату и чек пробивает кассовый агент. Срок жизни `screen`: состояние живёт, пока
 * открыто окно (хук продажи сбрасывает его при закрытии), в хранилище браузера не попадает; что именно пробивалось, переживает F5
 * отдельным слайсом снимка. Правила переходов — чистая функция `ownSaleReducer` (`saleState.ts`), слайс только раскладывает по источникам.
 */
export interface OwnSaleSliceState {
  byResource: Record<string, OwnSaleState>
}

const initialState: OwnSaleSliceState = { byResource: {} }

type Payload<T = unknown> = PayloadAction<{ resourceId: string } & T>

function apply(state: OwnSaleSliceState, resourceId: string, action: OwnSaleAction): void {
  const key = ownResourceKey(resourceId)
  const stored = state.byResource[key]
  const before = stored ?? initialOwnSale
  const after = ownSaleReducer(before, action)
  if (after !== before) state.byResource[key] = after
}

const slice = createSlice({
  name: 'own/sale',
  initialState,
  reducers: {
    ownProductAdded(state, { payload }: Payload<{ product: SaleProduct }>) {
      apply(state, payload.resourceId, { type: 'product-added', product: payload.product })
    },
    ownQuantityTyped(state, { payload }: Payload<{ lineId: number; text: string }>) {
      apply(state, payload.resourceId, { type: 'quantity-typed', lineId: payload.lineId, text: payload.text })
    },
    ownQuantityStepped(state, { payload }: Payload<{ lineId: number; delta: 1 | -1 }>) {
      apply(state, payload.resourceId, { type: 'quantity-stepped', lineId: payload.lineId, delta: payload.delta })
    },
    ownLineRemoved(state, { payload }: Payload<{ lineId: number }>) {
      apply(state, payload.resourceId, { type: 'line-removed', lineId: payload.lineId })
    },
    ownLineSelected(state, { payload }: Payload<{ lineId: number }>) {
      apply(state, payload.resourceId, { type: 'line-selected', lineId: payload.lineId })
    },
    ownPaymentKindChosen(state, { payload }: Payload<{ kind: OwnPaymentKind }>) {
      apply(state, payload.resourceId, { type: 'payment-kind-chosen', kind: payload.kind })
    },
    ownContactChanged(state, { payload }: Payload<{ text: string }>) {
      apply(state, payload.resourceId, { type: 'contact-changed', text: payload.text })
    },
    ownSearchChanged(state, { payload }: Payload<{ text: string }>) {
      apply(state, payload.resourceId, { type: 'search-changed', text: payload.text })
    },
    ownPriceTypeChosen(state, { payload }: Payload<{ key: string | null }>) {
      apply(state, payload.resourceId, { type: 'price-type-chosen', key: payload.key })
    },
    ownSaleCleared(state, { payload }: Payload) {
      apply(state, payload.resourceId, { type: 'cleared' })
    },
    ownPayStarted(state, { payload }: Payload<{ kind: OwnPaymentKind; totalKopecks: number }>) {
      apply(state, payload.resourceId, { type: 'pay-started', kind: payload.kind, totalKopecks: payload.totalKopecks })
    },
    ownPayAborted(state, { payload }: Payload) {
      apply(state, payload.resourceId, { type: 'pay-aborted' })
    },
    ownSaleSettled(state, { payload }: Payload<{ verdict: SaleVerdict }>) {
      apply(state, payload.resourceId, { type: 'settled', verdict: payload.verdict })
    },
    ownNewSaleStarted(state, { payload }: Payload) {
      apply(state, payload.resourceId, { type: 'new-sale' })
    },
    ownSaleParked(state, { payload }: Payload) {
      apply(state, payload.resourceId, { type: 'parked' })
    },
    ownCartRestored(state, { payload }: Payload<{ cart: Cart; paymentKind: OwnPaymentKind }>) {
      apply(state, payload.resourceId, { type: 'restored', cart: payload.cart, paymentKind: payload.paymentKind })
    },
    /** Окно закрыто: продажа этого окна не переходит в следующее (после F5 корзину возвращает снимок, а не слайс). */
    ownSaleWindowClosed(state, { payload }: Payload) {
      delete state.byResource[ownResourceKey(payload.resourceId)]
    },
  },
  extraReducers: (builder) => {
    builder.addCase(userScopeCleared, () => initialState)
    builder.addCase(resourceScopeCleared, (state, action) => {
      delete state.byResource[ownResourceKey(action.payload.resourceId)]
    })
  },
})

export const {
  ownProductAdded,
  ownQuantityTyped,
  ownQuantityStepped,
  ownLineRemoved,
  ownLineSelected,
  ownPaymentKindChosen,
  ownContactChanged,
  ownSearchChanged,
  ownPriceTypeChosen,
  ownSaleCleared,
  ownPayStarted,
  ownPayAborted,
  ownSaleSettled,
  ownNewSaleStarted,
  ownSaleParked,
  ownCartRestored,
  ownSaleWindowClosed,
} = slice.actions
export const ownSaleSliceReducer = slice.reducer

/** Продажа источника; нет записи — чистая продажа. */
export const saleOf = (state: OwnSaleSliceState, resourceId: string): OwnSaleState => state.byResource[ownResourceKey(resourceId)] ?? initialOwnSale
